/**
 * Thin wrapper around the browser SpeechRecognition API (Chrome, Edge, Safari,
 * mobile Chrome, iOS Safari). Exposes a tap-to-toggle recognizer that streams
 * interim text and emits a final transcript when recognition ends.
 */

export function isSupported() {
    if (typeof window === 'undefined') return false;
    return 'SpeechRecognition' in window || 'webkitSpeechRecognition' in window;
}

export function createSpeechRecognizer({ locale, onInterim, onFinal, onStart, onEnd, onError }) {
    if (!isSupported()) return null;

    const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new Ctor();
    recognition.lang = mapLocale(locale);
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.maxAlternatives = 1;

    let active = false;
    let finalTranscript = '';

    recognition.addEventListener('start', () => {
        active = true;
        finalTranscript = '';
        if (onStart) onStart();
    });

    recognition.addEventListener('result', (event) => {
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
            const result = event.results[i];
            const transcript = (result[0] && result[0].transcript) || '';
            if (result.isFinal) {
                finalTranscript += transcript;
            } else {
                interim += transcript;
            }
        }
        const preview = (finalTranscript + interim).trim();
        if (onInterim) onInterim(preview);
    });

    recognition.addEventListener('end', () => {
        active = false;
        const text = (finalTranscript || '').trim();
        if (onEnd) onEnd();
        if (text && onFinal) onFinal(text);
    });

    recognition.addEventListener('error', (event) => {
        active = false;
        if (onError) onError((event && event.error) || 'unknown');
    });

    return {
        start() {
            if (active) return;
            try {
                recognition.start();
            } catch (err) {
                if (onError) onError(String(err && err.message ? err.message : err));
            }
        },
        stop() {
            if (!active) return;
            try {
                recognition.stop();
            } catch (_err) {
                /* ignore */
            }
        },
        abort() {
            if (!active) return;
            try {
                recognition.abort();
            } catch (_err) {
                /* ignore */
            }
            active = false;
        },
        isActive() {
            return active;
        },
    };
}

function mapLocale(locale) {
    const code = typeof locale === 'string' ? locale.slice(0, 2).toLowerCase() : '';
    if (code === 'fr') return 'fr-FR';
    return 'en-US';
}
